import * as React from 'react';
import { Grid2 as Grid, Card, CardContent, Tab, Tabs, TabProps } from '@mui/material';
import { ActionRequest } from '../../actions';
import { Loading, LineChart } from '../common';
import moment, { Moment } from 'moment';
import { styled } from '@mui/styles';
import { AxisValueFormatterContext } from '@mui/x-charts/models/axis';
import { getDifferenceText, truncateNumber } from '../../utils';
import { ReportFilterDates } from './ReportFilterDates';

interface Props {
    readonly styled: StyledProps;
}

type ReportType = 'TotalRevenue' | 'PurchaseRevenue' | 'AdRevenue' | 'ARPU' | 'ARPPU';
interface State {
    readonly loading: boolean;
    readonly loadingReport: boolean;
    readonly startTimeSpam: Moment;
    readonly endTimeSpam: Moment;
    readonly reportType: ReportType;
    readonly revenueReport?: AnalyticsModels.RevenuePeriodReport;
    readonly currentData?: AnalyticsModels.DayValueReport[];
    readonly precedingData?: AnalyticsModels.DayValueReport[];
}

export class RevenueReport extends React.PureComponent<Props, State> {
    state: State = {
        loading: true,
        loadingReport: true,
        startTimeSpam: moment.utc().subtract(7, 'days'),
        endTimeSpam: moment.utc(),
        reportType: 'TotalRevenue',
    };

    componentDidMount() {
        this.getInitialData().then();
    }

    render() {
        const { styled } = this.props;
        const { loading, loadingReport, startTimeSpam, endTimeSpam, reportType, revenueReport, currentData, precedingData } = this.state;

        let selected = 0;
        switch (reportType) {
            case 'TotalRevenue':
                selected = 0;
                break;
            case 'PurchaseRevenue':
                selected = 1;
                break;
            case 'AdRevenue':
                selected = 2;
                break;
            case 'ARPU':
                selected = 3;
                break;
            case 'ARPPU':
                selected = 4;
                break;
        }

        const changeSubTab = (event: any, value: any) => {
            switch (value) {
                case 0:
                    this.setState({
                        reportType: 'TotalRevenue',
                    });
                    this.getRevenueDayData('totalRevenue').then();
                    break;
                case 1:
                    this.setState({
                        reportType: 'PurchaseRevenue',
                    });
                    this.getRevenueDayData('purchaseRevenue').then();
                    break;
                case 2:
                    this.setState({
                        reportType: 'AdRevenue',
                    });
                    this.getRevenueDayData('totalAdRevenue').then();
                    break;
                case 3:
                    this.setState({
                        reportType: 'ARPU',
                    });
                    this.getRevenueDayData('averagePurchaseRevenuePerUser').then();
                    break;
                case 4:
                    this.setState({
                        reportType: 'ARPPU',
                    });
                    this.getRevenueDayData('averagePurchaseRevenuePerPayingUser').then();
                    break;
            }
        };

        const daysDiff = endTimeSpam.diff(startTimeSpam, 'days') + 1;

        const onFilterDates = (start: Moment, end: Moment) => {
            this.setState({ startTimeSpam: start, endTimeSpam: end});
            this.getInitialData(start, end).then();
        };

        return (
            <Card style={{ padding: 0 }}>
                <CardContent style={{ padding: '0px 10px' }}>
                    {loading && (
                        <div style={{ height: 380 }}>
                            <Loading styled={styled} />
                        </div>
                    )}
                    {!loading && revenueReport && (
                        <Grid container={true} justifyContent="center" spacing={0}>
                            <Grid size={12}>
                                <StyledTabs
                                    value={selected}
                                    onChange={changeSubTab}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    variant="scrollable"
                                    allowScrollButtonsMobile={true}
                                    scrollButtons="auto"
                                >
                                    <StyledTab
                                        styled={styled}
                                        title={'Total Revenue'}
                                        subTitle={`$${truncateNumber(revenueReport.current.total)}`}
                                        difference={getDifferenceText(revenueReport.current.total, revenueReport.preceding.total)}
                                        value={0}
                                    />
                                    <StyledTab
                                        styled={styled}
                                        title={'Purchase Revenue'}
                                        subTitle={`$${truncateNumber(revenueReport.current.purchase)}`}
                                        difference={getDifferenceText(revenueReport.current.purchase, revenueReport.preceding.purchase)}
                                        value={1}
                                    />
                                    <StyledTab
                                        styled={styled}
                                        title={'Ad Revenue'}
                                        subTitle={`$${truncateNumber(revenueReport.current.ads)}`}
                                        difference={getDifferenceText(revenueReport.current.ads, revenueReport.preceding.ads)}
                                        value={2}
                                    />
                                    <StyledTab
                                        styled={styled}
                                        title={'ARPU'}
                                        subTitle={`$${truncateNumber(revenueReport.current.arpu)}`}
                                        difference={getDifferenceText(revenueReport.current.arpu, revenueReport.preceding.arpu)}
                                        value={3}
                                    />
                                    <StyledTab
                                        styled={styled}
                                        title={'ARPPU'}
                                        subTitle={`$${truncateNumber(revenueReport.current.arppu)}`}
                                        difference={getDifferenceText(revenueReport.current.arppu, revenueReport.preceding.arppu)}
                                        value={4}
                                    />
                                </StyledTabs>
                            </Grid>
                            <Grid size={12}>
                                {loadingReport && !currentData && !precedingData && (
                                    <div style={{ height: 300 }}>
                                        <Loading styled={styled} />
                                    </div>
                                )}
                                {!loadingReport && currentData && precedingData && (
                                    <LineChart
                                        styled={styled}
                                        series={[
                                            {
                                                curve: 'linear',
                                                data: currentData.map(d => d.value),
                                                color: '#1973e8',
                                                id: 'current',
                                                label: 'Current Period',
                                            },
                                            {
                                                curve: 'linear',
                                                data: precedingData.map(d => d.value),
                                                color: '#1973e8',
                                                id: 'preceding',
                                                label: 'Preceding Period',
                                            },
                                        ]}
                                        xAxis={[
                                            {
                                                scaleType: 'point',
                                                data: currentData.map(d => d.day),
                                                valueFormatter: (value: string, context: AxisValueFormatterContext) =>
                                                    context.location === 'tick' ? moment(value).format('MMM D') :
                                                        `${moment(value).format('MMM D')} vs ${moment(value).subtract(daysDiff, 'days').format('MMM D')}`
                                                ,
                                            }
                                        ]}
                                        yAxis={[
                                            {
                                                valueFormatter: (value: number, context: AxisValueFormatterContext) => `$${truncateNumber(value)}`,
                                                min: 0,
                                            }
                                        ]}
                                        height={300}
                                        grid={{ horizontal: true }}
                                        slotProps={{
                                            legend: {
                                                hidden: true,
                                            }
                                        }}
                                        sx={{
                                            '& .MuiChartsAxis-tickLabel': {
                                                fill: styled.themeMode === 'light' ? '#7d8085' : '#a7aaae !important',
                                            },
                                            '& .MuiChartsAxis-tick': {
                                                display: 'none',
                                            },
                                            '& .MuiChartsAxis-line': {
                                                display: 'none',
                                            },
                                            '& .MuiChartsGrid-line': {
                                                stroke: styled.themeMode === 'light' ? '#eeeeee' : '#303030 !important',
                                            },
                                            '.MuiLineElement-series-preceding': {
                                                strokeDasharray: '5 5',
                                                strokeWidth: 1,
                                            },
                                        }}
                                    />
                                )}
                            </Grid>
                            <Grid size={12}>
                                <ReportFilterDates
                                    styled={this.props.styled}
                                    start={startTimeSpam}
                                    end={endTimeSpam}
                                    onFilter={onFilterDates}
                                />
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>
        );
    }

    private async getInitialData(start?: Moment, end?: Moment): Promise<void> {
        await Promise.all([
            this.getRevenueData(start, end),
            this.getRevenueDayData('totalRevenue', start, end)
        ]);
    }

    private async getRevenueData(start?: Moment, end?: Moment): Promise<void> {
        this.setState({
            loading: true,
            currentData: undefined,
            precedingData: undefined,
        });
        const startTimeSpam = start || this.state.startTimeSpam;
        const endTimeSpam = end || this.state.endTimeSpam;
        const url = `analytics/revenueReport`;
        const revenueReport = await ActionRequest.get<AnalyticsModels.RevenuePeriodReport>(
            url,
            {
                startDate: startTimeSpam.format('YYYY-MM-DD'),
                endDate: endTimeSpam.format('YYYY-MM-DD'),
            }
        );
        this.setState({
            loading: false,
            revenueReport: revenueReport || undefined,
        });
    }

    private async getRevenueDayData(metric: string, start?: Moment, end?: Moment): Promise<void> {
        this.setState({
            loadingReport: true,
            currentData: undefined,
            precedingData: undefined,
        });
        const startTimeSpam = start || this.state.startTimeSpam;
        const endTimeSpam = end || this.state.endTimeSpam;
        const url = `analytics/revenueDayReport`;
        const data = await ActionRequest.get<AnalyticsModels.DayValueDoubleReport>(
            url,
            {
                startDate: startTimeSpam.format('YYYY-MM-DD'),
                endDate: endTimeSpam.format('YYYY-MM-DD'),
                metric,
            }
        );
        this.setState({
            loadingReport: false,
            currentData: data?.current || undefined,
            precedingData: data?.preceding || undefined,
        });
    }
}

const StyledTabs = styled(Tabs)(({
    padding: '0px 5px',
    height: 80,
    borderTop: 'none',
    borderBottom: 'none',
}));

interface StyledTabProps extends TabProps {
    readonly styled: StyledProps;
    readonly title: string;
    readonly subTitle: string;
    readonly difference: React.ReactNode;
}
const StyledTab = styled((props: StyledTabProps) => (
    <Tab
        {...props}
        label={(
            <div>
                <div>
                    {props.title}
                </div>
                <div style={{ textAlign: 'left', fontWeight: 400, fontSize: 22, color: props.styled.themeMode === 'light' ? '#202124' : 'white' }}>
                    {props.subTitle}
                </div>
                <div style={{ textAlign: 'left', fontSize: 12 }}>
                    {props.difference}
                </div>
            </div>
        )}
        sx={{
            height: 78,
            fontSize: 14,
            padding: '4px 10px',
            margin: '0px 10px',
            '&.MuiTab-root': {
                color: props.styled.themeMode === 'light' ? '#5f6368' : '#8a8e93',
                '&:hover': {
                    backgroundColor: props.styled.themeMode === 'light' ? '#f1f2f3' : '#292b2c',
                }
            },
            '&.Mui-selected': {
                color: props.styled.themeMode === 'light' ? '#1a73e8' : '#6ba5ff',
                borderTop: props.styled.themeMode === 'light' ? '4px solid #1a73e8' : '4px solid #6ba5ff',
                borderBottom: 'none',
                '&:hover': {
                    backgroundColor: props.styled.themeMode === 'light' ? '#f1f4f9' : '#25282f',
                }
            }
        }}
    >
        {props.children}
    </Tab>
))(() => ({}));